import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/api';

interface Author {
  firstName?: string;
  lastName?: string;
  teamName: string;
}

interface NewsPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  headerImage?: string;
  publishDate: string;
  status: 'draft' | 'published';
  author: Author;
  createdAt: string;
  updatedAt: string;
}

interface NewsResponse {
  posts: NewsPost[];
  totalCount: number;
  hasMore: boolean;
}

const AdminNews: React.FC = () => {
  const [newsData, setNewsData] = useState<NewsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'published'>('all');
  const [deleteModalPost, setDeleteModalPost] = useState<NewsPost | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNews();
  }, [filterStatus]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({ limit: '50' });
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      const response = await apiClient.get(`/news/admin/all?${params}`);
      setNewsData(response.data);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load news posts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (post: NewsPost) => {
    try {
      await apiClient.delete(`/news/admin/${post.id}`);
      setDeleteModalPost(null);
      fetchNews(); // Refresh the list
    } catch (error: any) {
      console.error('Delete error:', error);
      setError(error.response?.data?.message || 'Failed to delete news post');
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAuthor = (author: Author): string => {
    if (author.firstName && author.lastName) {
      return `${author.firstName} ${author.lastName}`;
    }
    return author.teamName;
  };

  const getExcerpt = (content: string, maxLength: number = 100): string => {
    const text = content.replace(/<[^>]*>/g, ''); // Strip HTML tags
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full";
    
    switch (status) {
      case 'published':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100`;
      case 'draft':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100`;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highlight"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Manage News Posts
            </h1>
            <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
              Create, edit, and manage news articles
            </p>
          </div>
          <button
                            onClick={() => navigate('/admin/news/create')}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300"
          >
            Create News Post
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex space-x-4">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filterStatus === 'all'
                ? 'bg-highlight text-black'
                : 'bg-light-surface dark:bg-dark-surface text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg'
            }`}
          >
            All Posts ({newsData?.totalCount || 0})
          </button>
          <button
            onClick={() => setFilterStatus('published')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filterStatus === 'published'
                ? 'bg-highlight text-black'
                : 'bg-light-surface dark:bg-dark-surface text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg'
            }`}
          >
            Published
          </button>
          <button
            onClick={() => setFilterStatus('draft')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              filterStatus === 'draft'
                ? 'bg-highlight text-black'
                : 'bg-light-surface dark:bg-dark-surface text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg'
            }`}
          >
            Drafts
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* News Posts Table */}
      {newsData && newsData.posts.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Author
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Publish Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {newsData.posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {post.title}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {getExcerpt(post.content)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {formatAuthor(post.author)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(post.status)}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {formatDate(post.publishDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    <Link
                      to={`/admin/news/edit/${post.id}`}
                      className="text-highlight hover:text-highlight/80"
                    >
                      Edit
                    </Link>
                    <span style={{ color: 'var(--text-secondary)' }}>|</span>
                    <Link
                      to={`/news/${post.slug}`}
                      target="_blank"
                      className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      View
                    </Link>
                    <span style={{ color: 'var(--text-secondary)' }}>|</span>
                    <button
                      onClick={() => setDeleteModalPost(post)}
                      className="text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-light-surface dark:bg-dark-surface rounded-lg">
          <p className="text-xl" style={{ color: 'var(--text-secondary)' }}>
            {filterStatus === 'all' ? 'No news posts yet.' : `No ${filterStatus} posts yet.`}
          </p>
          <Link
            to="/admin/news/create"
            className="mt-4 inline-block bg-highlight text-black px-4 py-2 rounded-md hover:bg-highlight/80 transition-colors"
          >
            Create Your First Post
          </Link>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-light-surface dark:bg-dark-surface rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--text-primary)' }}>
              Delete News Post
            </h3>
            <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
              Are you sure you want to delete "{deleteModalPost.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteModalPost(null)}
                className="px-4 py-2 border border-light-border dark:border-dark-border rounded-md hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                style={{ color: 'var(--text-primary)' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteModalPost)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNews; 